import * as React from "react";

import Form from "@ant-design/compatible/es/form";
import * as DV from "@impero-com/common/utils/dataView";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { ButtonGroup, SelectedTabParam, Tabs, TabSpec } from "@impero-com/ui";

import { EntityView } from "api/generated/model";
import { Drawer } from "components/drawer";
import * as R from "org/risks/types";
import { UpdateRootNodeLinkedResourcesCallback } from "org/risks/widgets/attachedFilesField";
import { initialEntityId, initialScope } from "org/risks/widgets/nodeScope";
import {
    FormCreate,
    FormRootComponentProps,
    hasError,
    subForm,
    ObjectError,
} from "utils/form";
import { Guid } from "utils/guid";

import * as T from "../types";
import * as U from "../utils";

import { CreateNodeDetailsTab, CreateNodeDetailsTabValues } from "./detailsTab";
import {
    CreateNodeEntitiesTab,
    CreateNodeEntitiesTabValues,
} from "./entitiesTab";
import * as theme from "./theme.css";

class CreateNodeDrawerForm extends React.PureComponent<
    CreateNodeDrawerProps,
    CreateNodeDrawerState
> {
    private tabFocusOnMountFlag = false;

    state: CreateNodeDrawerState = {
        currentTabId: NODE_DETAILS_DRAWER_TAB,
    };

    componentDidMount = () => {
        this.tabFocusOnMountFlag = true;
    };

    render = () => {
        const { currentTabId } = this.state;
        return (
            <Drawer
                onCancel={this.props.onClose}
                buildHeader={this.buildHeader}
                buildFooter={this.buildFooter}
            >
                <Form>{this.renderTabs[currentTabId]()}</Form>
            </Drawer>
        );
    };

    renderTabs = {
        [NODE_DETAILS_DRAWER_TAB]: () => {
            const {
                form,
                nodeType,
                parentNode,
                riskDirectoryView,
                metadataExtractors,
                entities,
                permission,
                lastAddedNodeScope,
                updateRootNodeLinkedResources,
            } = this.props;
            return (
                <CreateNodeDetailsTab
                    form={form}
                    nodeType={nodeType}
                    parentNode={parentNode}
                    riskDirectoryView={riskDirectoryView}
                    metadataExtractors={metadataExtractors}
                    entities={entities}
                    permission={permission}
                    buildNode={this.buildNode}
                    focusOnMount={this.tabFocusOnMount}
                    initialScope={lastAddedNodeScope}
                    updateRootNodeLinkedResources={
                        updateRootNodeLinkedResources
                    }
                />
            );
        },
        [ENTITIES_DRAWER_TAB]: () => {
            const { form, ancestorsEntities, entities } = this.props;
            return (
                <CreateNodeEntitiesTab
                    form={form}
                    ancestorsEntities={ancestorsEntities}
                    entities={entities}
                />
            );
        },
    };

    private buildHeader = () => {
        const {
            nodeType,
            ancestorsEntities,
            form,
            permission,
            lastAddedNodeScope,
        } = this.props;
        const { currentTabId } = this.state;
        const nodeEntities = subForm("entities", form).getFieldValue(
            "nodeEntities"
        );
        const scope = initialScope(
            permission,
            subForm("details", form).getFieldValue("scope") ??
                lastAddedNodeScope
        );
        const tabs: Array<TabSpec> = [];
        switch (nodeType) {
            case "Process":
                tabs.push({
                    id: NODE_DETAILS_DRAWER_TAB,
                    label: t("RiskDirectory_NodeUpdate_ProcessDetails"),
                    onClick: this.handleDrawerTabClick,
                });
                break;
            case "Risk":
                tabs.push({
                    id: NODE_DETAILS_DRAWER_TAB,
                    label: t("RiskDirectory_NodeUpdate_RiskDetails"),
                    onClick: this.handleDrawerTabClick,
                });
                break;
            default:
                return assertNever(nodeType);
        }
        if (scope === "Global") {
            tabs.push({
                id: ENTITIES_DRAWER_TAB,
                label: `${t("RiskMapResponses_RiskMapEntities")} (${
                    Object.keys(ancestorsEntities).length +
                    (nodeEntities ?? []).length
                })`,
                onClick: this.handleDrawerTabClick,
            });
        }
        // Guard against incorrect code (e.g. incorrect default tab)
        if (!(tabs.findIndex((tab) => tab.id === currentTabId) >= 0)) {
            throw new Error("Unexpected tab ID " + currentTabId);
        }
        return (
            <Tabs
                className={theme.drawerTabs}
                tabSpecs={tabs}
                currentTabId={currentTabId}
            />
        );
    };
    private buildFooter = () => {
        return (
            <ButtonGroup
                buttonSpecs={[
                    {
                        id: "create-node-save",
                        label: t("Confirm"),
                        onClick: this.saveNode,
                        type: "primary",
                    },
                    {
                        id: "create-node-cancel",
                        label: t("Cancel"),
                        onClick: this.props.onClose,
                        type: "link",
                    },
                ]}
            />
        );
    };
    private handleDrawerTabClick = (newSelectedTab: SelectedTabParam) => {
        this.setState({
            currentTabId: newSelectedTab.key as DrawerTabId,
        });
    };
    private buildNode = (
        values?: CreateNodeDrawerValues
    ): R.NewRiskDirectoryNode => {
        const {
            id,
            form,
            nodeType,
            parentNode,
            permission,
            lastAddedNodeScope,
        } = this.props;

        const content = values ?? form.getFieldsValue();

        let scope: R.NewScope;
        const scopeType = initialScope(
            permission,
            content.details?.scope ?? lastAddedNodeScope
        );
        switch (scopeType) {
            case "Global":
                scope = {
                    type: "NewGlobal",
                    entityIds: content.entities?.nodeEntities || [],
                };
                break;
            case "Local":
                scope = {
                    type: "NewLocal",
                    entityId: initialEntityId(
                        permission,
                        content.details?.scope ?? lastAddedNodeScope
                    ),
                };
                break;
            default:
                assertNever(scopeType);
        }

        return {
            type: nodeType,
            id,
            parentNodeId: parentNode.id,
            content: {
                name: content.details?.name || "",
                description: content.details?.description || "",
                metadataValues: content.details?.metadataValues
                    ? U.extractMetadataInstanceValues(
                          content.details.metadataValues
                      )
                    : {},
                scope,
            },
        };
    };
    private saveNode = () => {
        const {
            form: { validateFields },
            onClose,
        } = this.props;
        validateFields({ force: true }, (errors: ObjectError, values) => {
            if (hasError(errors)) {
                return;
            }
            this.props.createNode(this.buildNode(values)).then(onClose);
        });
    };

    private tabFocusOnMount = () => {
        const tfom = this.tabFocusOnMountFlag;
        if (tfom) {
            this.tabFocusOnMountFlag = false;
        }
        return tfom;
    };
}

interface CreateNodeDrawerProps extends FormRootComponentProps<CreateNodeDrawerValues> {
    id: Guid;
    onClose: () => void;
    parentNode: R.ParentNode;
    nodeType: "Process" | "Risk";
    metadataExtractors: Array<
        DV.DataExtractor<T.NodeWithMD, T.MetadataInstance>
    >;
    ancestorsEntities: Record<number, string>;
    entities: Record<number, EntityView>;
    permission: T.NodeCreationAllowedPermission;
    riskDirectoryView: R.RiskDirectoryView;
    createNode: (newNode: R.NewRiskDirectoryNode) => Promise<void>;
    lastAddedNodeScope?: T.NodeScope;
    updateRootNodeLinkedResources: UpdateRootNodeLinkedResourcesCallback;
}

interface CreateNodeDrawerState {
    currentTabId: DrawerTabId;
}

interface CreateNodeDrawerValues {
    details: CreateNodeDetailsTabValues;
    entities: CreateNodeEntitiesTabValues;
}

const NODE_DETAILS_DRAWER_TAB = "node-details-tab";
const ENTITIES_DRAWER_TAB = "entities-tab";

type DrawerTabId = typeof NODE_DETAILS_DRAWER_TAB | typeof ENTITIES_DRAWER_TAB;

const CreateNodeDrawer = FormCreate<
    CreateNodeDrawerValues,
    CreateNodeDrawerProps
>()(CreateNodeDrawerForm);

export {
    CreateNodeDrawer,
    type CreateNodeDrawerProps,
    type CreateNodeDrawerState,
};
